import { ShaderModule, ShaderRenderContext } from './index';

/**
 * Planck Shear Shader Mode
 *
 * Simulates spacetime breaking into discrete Planck quanta (5.39e-44 s).
 * Features:
 * - Discrete quantum Planck lattice slicing with gridDensity spacing
 * - Coordinate shearing along horizontal spatial bands with pseudo-random phase snap
 * - Chromatic aberration slippage (cyan, crimson/magenta, violet phase displacement)
 * - Micro-quantum ticks and stipple energy pulses at lattice intersections
 */
export class PlanckShearShader implements ShaderModule {
  readonly name = 'planck_shear' as const;

  // Pseudo-random deterministic hash for Planck quantum intervals
  private hash(n: number): number {
    const sinVal = Math.sin(n * 12.9898) * 43758.5453;
    return sinVal - Math.floor(sinVal);
  }

  render(context: ShaderRenderContext): void {
    const { ctx, width, height, time, uniforms, reducedMotion } = context;
    const { speed, intensity, distortion, gridDensity } = uniforms;

    const animSpeed = reducedMotion ? speed * 0.2 : speed;
    const t = time * animSpeed;

    // 1. Dark Void Base Layer
    ctx.fillStyle = '#05070a';
    ctx.fillRect(0, 0, width, height);

    // Subtle radial gradient in void center
    const cx = width * 0.5;
    const cy = height * 0.5;
    const bgGrad = ctx.createRadialGradient(cx, cy, 20, cx, cy, Math.max(width, height) * 0.7);
    bgGrad.addColorStop(0, 'rgba(10, 16, 28, 0.45)');
    bgGrad.addColorStop(1, 'rgba(5, 7, 10, 0.95)');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, width, height);

    // 2. Discrete Planck Lattice Grid
    // Clamped density determines the number of Planck slices across viewport
    const density = Math.max(10, Math.min(80, Math.round(gridDensity)));
    const stepY = Math.max(8, height / density);
    const stepX = Math.max(8, width / (density * (width / Math.max(1, height))));

    const chromaOffset = (1.5 + distortion * 6.5) * (reducedMotion ? 0.3 : 1.0);

    ctx.save();

    // 3. Horizontal Coordinate Shearing Bands
    // Each band computes a quantum phase slice and shears along the x-axis
    for (let y = 0; y < height; y += stepY) {
      const bandIndex = Math.floor(y / stepY);
      // Discrete time quantization: snaps state at discrete Planck moments
      const quantizedTick = Math.floor(t * 10);
      const bandHash = this.hash(bandIndex * 19.31 + quantizedTick * 7.13);

      // Determine if this band experiences Planck shearing
      const shearThreshold = 0.78 - distortion * 0.35;
      const isSheared = bandHash > shearThreshold;

      let shearX = 0;
      if (isSheared && !reducedMotion) {
        const harmonic = Math.sin(bandIndex * 0.45 + t * 4.5);
        shearX = harmonic * (12 + distortion * 48) * (bandHash - 0.5) * 2;
      }

      const bandAlpha = (0.08 + intensity * 0.18) * (isSheared ? 1.6 : 0.9);

      // Chromatic Aberration Slippage Pass 1: Cyan channel (negative offset)
      ctx.strokeStyle = `rgba(0, 243, 255, ${Math.min(1, bandAlpha * 0.9)})`;
      ctx.lineWidth = isSheared ? 1.5 : 1.0;
      ctx.beginPath();
      ctx.moveTo(0 + shearX - chromaOffset, y);
      ctx.lineTo(width + shearX - chromaOffset, y);
      ctx.stroke();

      // Chromatic Aberration Slippage Pass 2: Crimson/Shear-magenta channel (positive offset)
      if (distortion > 0.08) {
        ctx.strokeStyle = `rgba(255, 42, 75, ${Math.min(1, bandAlpha * 0.75 * distortion)})`;
        ctx.beginPath();
        ctx.moveTo(0 + shearX + chromaOffset, y);
        ctx.lineTo(width + shearX + chromaOffset, y);
        ctx.stroke();
      }

      // Discrete Slicing Glitch Rectangles
      if (isSheared && distortion > 0.25 && !reducedMotion) {
        const glitchWidth = (0.15 + bandHash * 0.35) * width;
        const glitchX = (bandHash * 0.8) * width + shearX;
        ctx.fillStyle = `rgba(0, 243, 255, ${0.04 + distortion * 0.08})`;
        ctx.fillRect(glitchX, y, glitchWidth, stepY * 0.7);
      }
    }

    // 4. Vertical Lattice Lines with Wave Deformation
    const vertAlpha = 0.05 + intensity * 0.12;
    for (let x = 0; x < width; x += stepX) {
      const lineIndex = Math.floor(x / stepX);
      const waveShift = Math.sin(lineIndex * 0.35 + t * 2.0) * distortion * 8;

      ctx.strokeStyle = `rgba(157, 78, 221, ${vertAlpha})`;
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(x + waveShift, 0);
      ctx.lineTo(x - waveShift, height);
      ctx.stroke();
    }

    // 5. Planck Micro-Ticks at Lattice Intersections
    // Discrete energy pulses blinking along intersections
    const tickIntervalX = stepX * 2;
    const tickIntervalY = stepY * 2;
    const maxTicksX = Math.ceil(width / tickIntervalX);
    const maxTicksY = Math.ceil(height / tickIntervalY);

    for (let ix = 0; ix < maxTicksX; ix++) {
      for (let iy = 0; iy < maxTicksY; iy++) {
        const px = ix * tickIntervalX;
        const py = iy * tickIntervalY;
        const tickPhase = Math.sin(ix * 1.7 + iy * 2.3 + t * 6.0);

        if (tickPhase > 0.45) {
          const tickBrightness = (tickPhase - 0.45) * 1.8 * intensity;
          const tickSize = 1.0 + tickBrightness * 1.5;

          ctx.fillStyle = `rgba(0, 243, 255, ${Math.min(1, tickBrightness)})`;
          ctx.beginPath();
          ctx.arc(px, py, tickSize, 0, Math.PI * 2);
          ctx.fill();

          // Amber high-tension tick
          if (tickPhase > 0.85 && distortion > 0.3) {
            ctx.fillStyle = `rgba(255, 183, 0, ${Math.min(1, tickBrightness * 1.2)})`;
            ctx.fillRect(px - 1, py - 1, 2, 2);
          }
        }
      }
    }

    ctx.restore();
  }
}
